import { Fragment, type ReactNode, type RefObject } from "react";
import { ChevronDown, X, Check } from "lucide-react";

import { CURRENT_BRAND } from "@/brand/config";
import { BrandMark } from "./BrandMark";

export function Frame({
  steps,
  currentStep,
  onClose,
  contentRef,
  rail,
  children,
}: {
  steps: string[];
  currentStep: number;
  onClose: () => void;
  // React 19: useRef<HTMLDivElement>(null) => RefObject<HTMLDivElement | null>.
  contentRef: RefObject<HTMLDivElement | null>;
  /** Persistent rail rendered on the left at ≥960px; hidden on mobile. */
  rail?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="stage">
      <div className="app">
        {rail}
        <div className="app__main">
          <Header onClose={onClose} />
          <Stepper steps={steps} current={currentStep} />
          <div className="content" role="main" ref={contentRef}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

function Header({ onClose }: { onClose: () => void }) {
  return (
    <header className="hdr">
      <a className="brand" href="/" aria-label={CURRENT_BRAND.displayName}>
        <BrandMark />
        <span className="brand__name">{CURRENT_BRAND.displayName}</span>
      </a>
      <div className="hdr__sp"></div>
      <button
        className="lang"
        aria-label="Idioma: español"
        title="Español (única opción en v1)"
      >
        es <ChevronDown size={16} strokeWidth={1.8} />
      </button>
      <button
        className="icon-btn"
        aria-label="Salir del proceso"
        onClick={onClose}
      >
        <X size={20} strokeWidth={1.7} />
      </button>
    </header>
  );
}

function Stepper({ steps, current }: { steps: string[]; current: number }) {
  return (
    <div className="stepper">
      <div className="stepper__track">
        {steps.map((s, i) => {
          const state = i < current ? "done" : i === current ? "current" : "pending";
          return (
            <Fragment key={s}>
              <span className={"step step--" + state}>
                <span className="step__dot">
                  {i < current ? (
                    <Check size={14} strokeWidth={2.4} />
                  ) : (
                    <>{i + 1}</>
                  )}
                </span>
                <span className="step__label">{s}</span>
              </span>
              {i < steps.length - 1 && (
                <span
                  className={"step__line" + (i < current ? " step__line--done" : "")}
                ></span>
              )}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

export function Footer({ children }: { children: ReactNode }) {
  return <footer className="ftr">{children}</footer>;
}

export function AbandonModal({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="modal">
      <div className="modal__backdrop" onClick={onCancel}></div>
      <div className="modal__card" role="dialog" aria-modal="true">
        <h2 className="modal__title">¿Salir del proceso?</h2>
        <p className="modal__body">
          Si sales ahora, tu cuenta quedará pendiente y se eliminará en 24
          horas si no completas tu activación.
        </p>
        <div className="modal__actions">
          <button className="btn btn--ghost" onClick={onCancel}>
            Cancelar
          </button>
          <button className="btn btn--danger" onClick={onConfirm}>
            Salir
          </button>
        </div>
      </div>
    </div>
  );
}

export function Toast({
  toast,
}: {
  toast: { msg: string; kind: "ok" | "warn" | "err" | "info" };
}) {
  return (
    <div className={"toast toast--" + toast.kind} role="status" aria-live="polite">
      {toast.msg}
    </div>
  );
}
